import React from 'react'
import { Trans } from 'react-i18next'

import { Skeleton, SkeletonGroup } from '@makyoapp/makyo-tw-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useCompBackgroundColor, useCompContext } from '@/context/__project__/CompContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ComponentSettingKey, FormComponentProps, ParentComponentProps } from '@/shared/builder/types'

import { CompFormWrapperCore } from '../core/forms/form-wrapper/wrapper'
import { CompForm } from './CompForm'

/**
 * Form Wrapper
 */
const FormWrapper = (props: ParentComponentProps<FormComponentProps>) => {
  // infos
  const compContext = useCompContext()
  const { currentParentBg } = useCompBackgroundColor()
  const { activeLayer } = useActiveLayerContext()
  const detailTableId = compContext.detailTableId
  const infosProps = { ...compContext, currentParentBg, activeLayer }

  // tableData
  const { dbClient, language, userSpecific, loadedTables, tablesMap } = useDbClientContext()
  const tableId = compContext.settings?.[ComponentSettingKey.FormDataSource] || ''
  const table = tableId ? tablesMap.get(tableId) : undefined

  const isLoadingTableData = detailTableId && detailTableId === tableId && !loadedTables.has(detailTableId)

  if (isLoadingTableData) {
    return (
      <div className='px-4 py-8'>
        <SkeletonGroup backgroundColor={currentParentBg}>
          <div className='space-y-6'>
            <Skeleton className='w-full h-11 rounded-lg' />
            <Skeleton className='w-full h-11 rounded-lg' />
            <div className='flex justify-end'>
              <Skeleton className='w-12 h-6 rounded-full' />
            </div>
            <Skeleton className='w-full h-11 rounded-lg' />
            <Skeleton className='w-full h-11 rounded-lg' />
            <div className='flex space-x-2'>
              {[...new Array(5)].map((_, i) => (
                <Skeleton key={i} className='w-8 h-8' />
              ))}
            </div>
            <Skeleton className='w-full h-12 rounded-lg bg-purple-600/30' />
          </div>
        </SkeletonGroup>
      </div>
    )
  }

  // todo: error state
  if (!table || !dbClient) {
    return (
      <div className='flex justify-center items-center py-8'>
        <Trans>COMP_TABLE_NOT_FOUND</Trans>
      </div>
    )
  }

  return (
    <CompFormWrapperCore infos={infosProps} tableData={{ dbTable: table, dbClient, tableId, language, userSpecific }}>
      {(wrapperProps) => (
        <CompForm
          {...wrapperProps}
          settings={compContext.settings}
          dataSource={tableId}
          dataSourceTable={table}
          showDebug={window.appMode === 'preview'}
        >
          {(formProps) => props.children(formProps)}
        </CompForm>
      )}
    </CompFormWrapperCore>
  )
}

const CompFormWrapper = React.memo(FormWrapper)

export default CompFormWrapper
